import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Send, Image, Trash2, MessageCircle, ThumbsUp, Heart, PartyPopper, Lightbulb, Laugh, ChevronDown, Loader2, UserCircle, Cake, Award, X, Quote, Sparkles, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const REACTION_MAP = {
  like: { icon: ThumbsUp, label: 'Like', color: 'text-blue-600', bg: 'bg-blue-100', hoverBg: 'hover:bg-blue-100' },
  love: { icon: Heart, label: 'Love', color: 'text-rose-500', bg: 'bg-rose-100', hoverBg: 'hover:bg-rose-100' },
  celebrate: { icon: PartyPopper, label: 'Celebrate', color: 'text-amber-600', bg: 'bg-amber-100', hoverBg: 'hover:bg-amber-100' },
  insightful: { icon: Lightbulb, label: 'Insightful', color: 'text-emerald-600', bg: 'bg-emerald-100', hoverBg: 'hover:bg-emerald-100' },
  funny: { icon: Laugh, label: 'Funny', color: 'text-purple-600', bg: 'bg-purple-100', hoverBg: 'hover:bg-purple-100' },
};

const POST_TYPE_CONFIG = {
  birthday: { bg: 'bg-[#FEF08A]', border: 'border-[#0A0A0A]', icon: Cake, iconColor: 'text-pink-600', badge: 'Birthday', badgeBg: 'bg-pink-200' },
  work_anniversary: { bg: 'bg-[#A7F3D0]', border: 'border-[#0A0A0A]', icon: Award, iconColor: 'text-emerald-700', badge: 'Work Anniversary', badgeBg: 'bg-emerald-200' },
  marriage_anniversary: { bg: 'bg-[#FECDD3]', border: 'border-[#0A0A0A]', icon: Heart, iconColor: 'text-rose-600', badge: 'Anniversary', badgeBg: 'bg-rose-200' },
};

const CARD = 'bg-white rounded-2xl border-2 border-[#0A0A0A] shadow-[4px_4px_0px_0px_rgba(10,10,10,1)]';
const PILL_BTN = 'border-2 border-[#0A0A0A] shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] rounded-full hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-all duration-200';

export default function TalentBuzz() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [newContent, setNewContent] = useState('');
  const [newImage, setNewImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [posting, setPosting] = useState(false);
  const [quote, setQuote] = useState(null);
  const [user, setUser] = useState(null);
  const fileRef = useRef(null);
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    const ud = localStorage.getItem('user');
    if (ud) setUser(JSON.parse(ud));
  }, []);

  const fetchPosts = useCallback(async (p = 1) => {
    try {
      const res = await axios.get(`${API}/talent-buzz/posts?page=${p}&limit=15`, { headers: getHeaders() });
      if (p === 1) setPosts(res.data.posts);
      else setPosts(prev => [...prev, ...res.data.posts]);
      setTotalPages(res.data.pages);
      setPage(p);
    } catch (e) { toast.error('Failed to load posts'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchPosts();
    axios.get(`${API}/talent-buzz/quote`).then(r => setQuote(r.data)).catch(() => {});
  }, []);

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setNewImage(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handlePost = async () => {
    if (!newContent.trim() && !newImage) return;
    setPosting(true);
    const formData = new FormData();
    formData.append('content', newContent);
    formData.append('post_type', 'general');
    if (newImage) formData.append('file', newImage);
    try {
      const res = await axios.post(`${API}/talent-buzz/posts`, formData, { headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' } });
      setPosts(prev => [res.data, ...prev]);
      setNewContent('');
      setNewImage(null);
      setImagePreview(null);
      toast.success('Posted!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to post'); }
    finally { setPosting(false); }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await axios.delete(`${API}/talent-buzz/posts/${postId}`, { headers: getHeaders() });
      setPosts(prev => prev.filter(p => p.id !== postId));
      toast.success('Post deleted');
    } catch (e) { toast.error('Failed to delete'); }
  };

  const handleEdit = async (postId, newContent) => {
    try {
      const res = await axios.put(`${API}/talent-buzz/posts/${postId}`, { content: newContent }, { headers: getHeaders() });
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, content: res.data.content, edited_at: res.data.edited_at } : p));
      toast.success('Post updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
  };

  const handleReact = async (postId, emoji) => {
    try {
      const res = await axios.post(`${API}/talent-buzz/posts/${postId}/react`, { emoji }, { headers: getHeaders() });
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, reactions: res.data.reactions, reaction_count: res.data.reaction_count } : p));
    } catch (e) { toast.error('Reaction failed'); }
  };

  const handleComment = async (postId, text) => {
    if (!text.trim()) return;
    try {
      const res = await axios.post(`${API}/talent-buzz/posts/${postId}/comments`, { text }, { headers: getHeaders() });
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, comments: [...(p.comments || []), res.data], comment_count: (p.comment_count || 0) + 1 } : p));
    } catch (e) { toast.error('Comment failed'); }
  };

  const handleDeleteComment = async (postId, commentId) => {
    try {
      await axios.delete(`${API}/talent-buzz/posts/${postId}/comments/${commentId}`, { headers: getHeaders() });
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, comments: (p.comments || []).filter(c => c.id !== commentId), comment_count: Math.max(0, (p.comment_count || 1) - 1) } : p));
    } catch (e) { toast.error('Failed to delete comment'); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 size={40} className="animate-spin text-amber-500" /></div>;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FEFCE8', fontFamily: 'Figtree, sans-serif' }} data-testid="talent-buzz-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-xl bg-[#FDE047] border-2 border-[#0A0A0A] shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] flex items-center justify-center">
            <Sparkles size={22} strokeWidth={2.5} className="text-[#0A0A0A]" />
          </div>
          <h1 className="text-4xl font-black tracking-tight text-[#0A0A0A]" style={{ fontFamily: 'Outfit, sans-serif' }}>Talent Buzz</h1>
        </div>
        <p className="text-[#737373] font-medium ml-[52px]">Share vibes, celebrate wins, connect with the crew</p>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Main Feed Column */}
        <div className="col-span-12 lg:col-span-8">
          {/* Composer */}
          <div className={`${CARD} p-6 mb-6`} data-testid="post-composer">
            <div className="flex gap-4">
              <BoldAvatar name={user?.name} photo={user?.photo_url} size={44} />
              <div className="flex-1">
                <textarea
                  value={newContent}
                  onChange={e => setNewContent(e.target.value)}
                  placeholder="What's on your mind? Share something awesome..."
                  className="w-full resize-none border-0 focus:ring-0 text-[15px] placeholder:text-[#A3A3A3] min-h-[70px] p-2 outline-none bg-transparent"
                  style={{ fontFamily: 'Figtree, sans-serif' }}
                  data-testid="post-input"
                />
                {imagePreview && (
                  <div className="relative mt-2 inline-block">
                    <img src={imagePreview} alt="Preview" className="max-h-48 rounded-xl border-2 border-[#0A0A0A]" />
                    <button onClick={() => { setNewImage(null); setImagePreview(null); }} className="absolute -top-2 -right-2 w-7 h-7 bg-[#0A0A0A] text-white rounded-full flex items-center justify-center hover:bg-red-500 transition-colors"><X size={14} strokeWidth={3} /></button>
                  </div>
                )}
                <div className="flex items-center justify-between mt-3 pt-3 border-t-2 border-dashed border-[#E5E5E5]">
                  <button onClick={() => fileRef.current?.click()} className={`flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full border-2 border-[#E5E5E5] text-[#737373] hover:border-[#0A0A0A] hover:text-[#0A0A0A] hover:bg-[#BFDBFE] transition-all`} data-testid="add-image-btn">
                    <Image size={18} strokeWidth={2.5} />Photo
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImageSelect} />
                  <button
                    onClick={handlePost}
                    disabled={posting || (!newContent.trim() && !newImage)}
                    className={`flex items-center gap-2 text-sm font-bold px-6 py-2 bg-[#A7F3D0] text-[#0A0A0A] ${PILL_BTN} disabled:opacity-40 disabled:hover:translate-y-0 disabled:hover:shadow-[2px_2px_0px_0px_rgba(10,10,10,1)]`}
                    data-testid="submit-post-btn"
                  >
                    {posting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} strokeWidth={2.5} />}Post
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Feed */}
          {posts.length === 0 ? (
            <div className={`${CARD} p-12 text-center`}>
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#FDE047] border-2 border-[#0A0A0A] shadow-[3px_3px_0px_0px_rgba(10,10,10,1)] flex items-center justify-center">
                <MessageCircle size={28} strokeWidth={2.5} className="text-[#0A0A0A]" />
              </div>
              <p className="text-lg font-bold text-[#0A0A0A]" style={{ fontFamily: 'Outfit, sans-serif' }}>No buzz yet!</p>
              <p className="text-sm text-[#737373] mt-1">Be the first to spark a conversation</p>
            </div>
          ) : (
            <div className="space-y-6" data-testid="posts-feed">
              {posts.map((post, idx) => (
                <div key={post.id} style={{ animationDelay: `${idx * 60}ms` }} className="animate-[fadeInUp_0.4s_ease-out_both]">
                  <PostCard post={post} user={user} onReact={handleReact} onComment={handleComment} onDelete={handleDelete} onDeleteComment={handleDeleteComment} onEdit={handleEdit} />
                </div>
              ))}
              {page < totalPages && (
                <div className="text-center py-4">
                  <button onClick={() => fetchPosts(page + 1)} className={`inline-flex items-center gap-2 text-sm font-bold px-6 py-2.5 bg-white text-[#0A0A0A] ${PILL_BTN}`} data-testid="load-more-btn">
                    <ChevronDown size={16} strokeWidth={2.5} />Load More Buzz
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="col-span-12 lg:col-span-4 space-y-6">
          {/* Quote of the Day */}
          {quote && (
            <div className="rounded-2xl border-2 border-[#0A0A0A] shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] overflow-hidden bg-[#E9D5FF] relative" data-testid="quote-of-day-widget">
              <div className="absolute top-2 right-4 opacity-[0.08]">
                <Quote size={120} strokeWidth={3} className="text-[#0A0A0A]" />
              </div>
              <div className="relative p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-7 h-7 rounded-lg bg-[#0A0A0A] flex items-center justify-center">
                    <Sparkles size={14} className="text-[#FDE047]" />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-widest text-[#0A0A0A]" style={{ fontFamily: 'Outfit, sans-serif' }}>Quote of the Day</span>
                </div>
                <p className="text-xl font-black leading-tight text-[#0A0A0A] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="quote-text">
                  "{quote.text}"
                </p>
                <p className="text-sm font-semibold text-[#404040]" data-testid="quote-author">— {quote.author}</p>
              </div>
            </div>
          )}

          {/* Community Card */}
          <CommunityWidget getHeaders={getHeaders} />

          {/* Quick Actions */}
          <div className={`${CARD} p-5`}>
            <span className="text-sm font-bold text-[#0A0A0A] block mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Quick Actions</span>
            <div className="space-y-2">
              <Link to="/profile" className={`flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-xl border-2 border-[#E5E5E5] text-[#404040] hover:border-[#0A0A0A] hover:bg-[#A7F3D0] transition-all w-full`}>
                <UserCircle size={16} strokeWidth={2.5} />My Profile
              </Link>
              <Link to="/employee-directory" className={`flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-xl border-2 border-[#E5E5E5] text-[#404040] hover:border-[#0A0A0A] hover:bg-[#BFDBFE] transition-all w-full`}>
                <Users size={16} strokeWidth={2.5} />Employee Directory
              </Link>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

function CommunityWidget({ getHeaders }) {
  const [community, setCommunity] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/talent-buzz/community`, { headers: getHeaders() })
      .then(r => setCommunity(r.data))
      .catch(() => {});
  }, []);

  if (!community) return null;

  const shown = expanded ? community.members : community.members.slice(0, 8);

  return (
    <div className={`${CARD} p-5`} data-testid="community-widget">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Users size={18} strokeWidth={2.5} className="text-[#0A0A0A]" />
          <span className="text-sm font-bold text-[#0A0A0A]" style={{ fontFamily: 'Outfit, sans-serif' }}>Our Community</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-xs font-bold text-emerald-700">{community.online} online</span>
        </div>
      </div>
      <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
        {shown.map(m => (
          <div key={m.id} className="flex items-center gap-2.5 p-2 rounded-xl" data-testid={`community-member-${m.id}`}>
            <div className="relative flex-shrink-0">
              <BoldAvatar name={m.name} photo={m.photo_url} size={34} />
              {m.is_online && <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-emerald-500 border-2 border-white" data-testid={`online-indicator-${m.id}`} />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-[#0A0A0A] truncate">{m.name}</p>
              <p className="text-[10px] text-[#737373] truncate">{m.designation || m.department || m.role?.replace('_', ' ')}</p>
            </div>
            {m.is_online ? (
              <span className="text-[9px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full border border-emerald-200 flex-shrink-0">Online</span>
            ) : (
              <span className="text-[9px] font-bold text-[#A3A3A3] bg-[#F5F5F5] px-2 py-0.5 rounded-full flex-shrink-0">Away</span>
            )}
          </div>
        ))}
      </div>
      {community.members.length > 8 && (
        <button onClick={() => setExpanded(!expanded)} className="mt-3 text-xs font-bold text-blue-600 hover:underline w-full text-center" data-testid="community-toggle">
          {expanded ? 'Show Less' : `View all ${community.total} members`}
        </button>
      )}
    </div>
  );
}

function PostCard({ post, user, onReact, onComment, onDelete, onDeleteComment, onEdit }) {
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [showReactions, setShowReactions] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const isOwn = user?.id === post.author_id;
  const isAdmin = user && ['admin', 'admin_support'].includes(user.role);
  const isSystem = post.author_id === 'system';
  const typeConfig = POST_TYPE_CONFIG[post.post_type];
  const timeAgo = getTimeAgo(post.created_at);
  const myReaction = Object.entries(post.reactions || {}).find(([, users]) => users.includes(user?.id))?.[0];

  const submitComment = () => {
    if (!commentText.trim()) return;
    onComment(post.id, commentText);
    setCommentText('');
    setShowComments(true);
  };

  const cardBg = typeConfig ? typeConfig.bg : 'bg-white';
  const cardBorder = 'border-2 border-[#0A0A0A]';

  return (
    <div className={`rounded-2xl ${cardBorder} shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] overflow-hidden ${cardBg}`} data-testid={`post-${post.id}`}>
      {/* Header */}
      <div className="px-6 pt-5 pb-2 flex items-start justify-between">
        <div className="flex items-center gap-3">
          {isSystem ? (
            <div className="w-11 h-11 rounded-xl bg-[#0A0A0A] border-2 border-[#0A0A0A] flex items-center justify-center text-[#FDE047] font-black text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>HR</div>
          ) : (
            <Link to={`/profile/${post.author_id}`}>
              <BoldAvatar name={post.author_name} photo={post.author_photo} size={44} />
            </Link>
          )}
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              {isSystem ? (
                <span className="text-sm font-bold text-[#0A0A0A]" style={{ fontFamily: 'Outfit, sans-serif' }}>HRassistance</span>
              ) : (
                <Link to={`/profile/${post.author_id}`} className="text-sm font-bold text-[#0A0A0A] hover:underline" style={{ fontFamily: 'Outfit, sans-serif' }}>{post.author_name}</Link>
              )}
              {typeConfig && (
                <span className={`text-[10px] font-bold uppercase px-2.5 py-0.5 rounded-full border-2 border-[#0A0A0A] ${typeConfig.badgeBg} flex items-center gap-1`}>
                  <typeConfig.icon size={10} strokeWidth={3} className={typeConfig.iconColor} />{typeConfig.badge}
                </span>
              )}
            </div>
            <p className="text-xs text-[#A3A3A3] font-medium mt-0.5">{post.author_designation ? `${post.author_designation} · ` : ''}{timeAgo}</p>
          </div>
        </div>
        {(isOwn || isAdmin) && !isSystem && (
          <div className="flex items-center gap-1">
            {isOwn && !editing && (
              <button onClick={() => { setEditing(true); setEditContent(post.content); }} className="text-[#D4D4D4] hover:text-blue-500 transition-colors p-1" data-testid={`edit-post-${post.id}`}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg>
              </button>
            )}
            <button onClick={() => onDelete(post.id)} className="text-[#D4D4D4] hover:text-red-500 transition-colors p-1" data-testid={`delete-post-${post.id}`}>
              <Trash2 size={16} strokeWidth={2.5} />
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="px-6 pb-3">
        {editing ? (
          <div className="space-y-2" data-testid={`edit-form-${post.id}`}>
            <textarea
              value={editContent}
              onChange={e => setEditContent(e.target.value)}
              className="w-full resize-none border-2 border-[#0A0A0A] rounded-xl p-3 text-[15px] text-[#262626] min-h-[80px] outline-none focus:border-blue-500"
              data-testid={`edit-input-${post.id}`}
            />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setEditing(false)} className={`text-xs font-bold px-4 py-1.5 rounded-full border-2 border-[#E5E5E5] text-[#737373] hover:border-[#0A0A0A] transition-all`} data-testid={`edit-cancel-${post.id}`}>Cancel</button>
              <button onClick={() => { onEdit(post.id, editContent); setEditing(false); }} disabled={!editContent.trim()} className={`text-xs font-bold px-4 py-1.5 bg-[#A7F3D0] text-[#0A0A0A] ${PILL_BTN} disabled:opacity-40`} data-testid={`edit-save-${post.id}`}>Save</button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-[15px] text-[#262626] whitespace-pre-wrap leading-relaxed">{post.content}</p>
            {post.edited_at && <p className="text-[10px] text-[#A3A3A3] mt-1 italic">edited</p>}
          </>
        )}
      </div>

      {/* Image */}
      {post.image_url && (
        <div className="px-6 pb-3">
          <img src={`${BACKEND}${post.image_url}`} alt="" className="w-full max-h-[500px] object-contain rounded-xl border-2 border-[#0A0A0A] bg-slate-100" data-testid={`post-image-${post.id}`} />
        </div>
      )}

      {/* Reaction Summary */}
      {post.reaction_count > 0 && (
        <div className="px-6 pb-2 flex items-center gap-2">
          <div className="flex -space-x-1">
            {Object.entries(post.reactions || {}).map(([emoji]) => {
              const R = REACTION_MAP[emoji];
              return R ? <span key={emoji} className={`w-6 h-6 rounded-full ${R.bg} border-2 border-white flex items-center justify-center`}><R.icon size={12} className={R.color} /></span> : null;
            })}
          </div>
          <span className="text-xs font-semibold text-[#737373]">{post.reaction_count}</span>
          {(post.comment_count || 0) > 0 && <span className="text-xs text-[#A3A3A3] ml-auto">{post.comment_count} comment{post.comment_count !== 1 ? 's' : ''}</span>}
        </div>
      )}

      {/* Actions Bar */}
      <div className="px-6 py-2.5 border-t-2 border-dashed border-[#E5E5E5] flex items-center gap-2">
        <div className="relative">
          <button
            onClick={() => myReaction ? onReact(post.id, myReaction) : setShowReactions(!showReactions)}
            onMouseEnter={() => setShowReactions(true)}
            className={`flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-full border-2 transition-all duration-200 ${
              myReaction
                ? `${REACTION_MAP[myReaction].color} ${REACTION_MAP[myReaction].bg} border-[#0A0A0A] shadow-[2px_2px_0px_0px_rgba(10,10,10,1)]`
                : 'text-[#737373] border-[#E5E5E5] hover:border-[#0A0A0A] hover:bg-[#BFDBFE]'
            }`}
            data-testid={`react-btn-${post.id}`}
          >
            {myReaction ? <>{React.createElement(REACTION_MAP[myReaction].icon, { size: 15, strokeWidth: 2.5 })}{REACTION_MAP[myReaction].label}</> : <><ThumbsUp size={15} strokeWidth={2.5} />Like</>}
          </button>
          {showReactions && (
            <div className="absolute bottom-full left-0 mb-2 bg-white rounded-2xl shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] border-2 border-[#0A0A0A] px-2 py-2 flex gap-1 z-50" onMouseLeave={() => setShowReactions(false)} data-testid="reaction-picker">
              {Object.entries(REACTION_MAP).map(([emoji, { icon: Icon, label, color, hoverBg }]) => (
                <button key={emoji} onClick={() => { onReact(post.id, emoji); setShowReactions(false); }} className={`p-2 rounded-xl ${hoverBg} transition-all hover:scale-125 ${color}`} title={label}>
                  <Icon size={22} strokeWidth={2.5} />
                </button>
              ))}
            </div>
          )}
        </div>
        <button onClick={() => setShowComments(!showComments)} className={`flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-full border-2 border-[#E5E5E5] text-[#737373] hover:border-[#0A0A0A] hover:bg-[#DDD6FE] transition-all duration-200`} data-testid={`comment-toggle-${post.id}`}>
          <MessageCircle size={15} strokeWidth={2.5} />Comment{post.comment_count > 0 ? ` (${post.comment_count})` : ''}
        </button>
      </div>

      {/* Comments Section */}
      {showComments && (
        <div className="px-6 py-4 bg-[#FAFAF9] border-t-2 border-dashed border-[#E5E5E5]" data-testid={`comments-section-${post.id}`}>
          {(post.comments || []).map(c => (
            <div key={c.id} className="flex gap-2.5 mb-3" data-testid={`comment-${c.id}`}>
              <Link to={`/profile/${c.author_id}`}><BoldAvatar name={c.author_name} photo={c.author_photo} size={30} /></Link>
              <div className="flex-1 bg-white rounded-xl px-3.5 py-2.5 border-2 border-[#E5E5E5]">
                <div className="flex items-center justify-between">
                  <Link to={`/profile/${c.author_id}`} className="text-xs font-bold text-[#0A0A0A] hover:underline" style={{ fontFamily: 'Outfit, sans-serif' }}>{c.author_name}</Link>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-[#A3A3A3] font-medium">{getTimeAgo(c.created_at)}</span>
                    {(c.author_id === user?.id || ['admin', 'admin_support'].includes(user?.role)) && (
                      <button onClick={() => onDeleteComment(post.id, c.id)} className="text-[#D4D4D4] hover:text-red-500 transition-colors"><Trash2 size={12} strokeWidth={2.5} /></button>
                    )}
                  </div>
                </div>
                <p className="text-[13px] text-[#404040] mt-1">{c.text}</p>
              </div>
            </div>
          ))}
          <div className="flex gap-2.5 mt-2">
            <BoldAvatar name={user?.name} photo={user?.photo_url} size={30} />
            <div className="flex-1 flex gap-2">
              <input
                value={commentText}
                onChange={e => setCommentText(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && submitComment()}
                placeholder="Write a comment..."
                className="flex-1 h-9 rounded-full bg-white border-2 border-[#E5E5E5] px-4 text-xs outline-none focus:border-[#0A0A0A] transition-colors font-medium"
                data-testid={`comment-input-${post.id}`}
              />
              <button onClick={submitComment} disabled={!commentText.trim()} className={`w-9 h-9 bg-[#A7F3D0] text-[#0A0A0A] rounded-full flex items-center justify-center disabled:opacity-30 border-2 border-[#0A0A0A] shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] hover:-translate-y-0.5 transition-all`} data-testid={`submit-comment-${post.id}`}>
                <Send size={13} strokeWidth={3} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BoldAvatar({ name, photo, size = 40 }) {
  if (photo) {
    return <img src={`${BACKEND}${photo}`} alt={name} className="rounded-xl object-cover flex-shrink-0 border-2 border-[#0A0A0A]" style={{ width: size, height: size }} />;
  }
  const colors = ['#A7F3D0', '#DDD6FE', '#FECDD3', '#FDE047', '#BFDBFE', '#FFDAB9'];
  const colorIdx = (name?.charCodeAt(0) || 0) % colors.length;
  return (
    <div className="rounded-xl border-2 border-[#0A0A0A] flex items-center justify-center font-black flex-shrink-0" style={{ width: size, height: size, backgroundColor: colors[colorIdx], fontSize: size * 0.4, fontFamily: 'Outfit, sans-serif' }}>
      {name?.charAt(0) || '?'}
    </div>
  );
}

function getTimeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

/* ======================== DASHBOARD WIDGET ======================== */
export function TalentBuzzWidget() {
  const [posts, setPosts] = useState([]);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/talent-buzz/widget`, { headers: getHeaders() }).then(r => setPosts(r.data)).catch(() => {}),
      axios.get(`${API}/talent-buzz/quote`).then(r => setQuote(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  if (loading) return null;

  return (
    <div className="space-y-4" data-testid="buzz-widget">
      {/* Quote Mini Widget */}
      {quote && (
        <div className="bg-[#E9D5FF] border border-purple-200 rounded-xl p-4 relative overflow-hidden" data-testid="buzz-quote-mini">
          <div className="absolute top-1 right-2 opacity-[0.06]"><Quote size={60} strokeWidth={3} /></div>
          <p className="text-xs font-bold uppercase tracking-wider text-purple-800 mb-2 flex items-center gap-1"><Sparkles size={12} />Quote of the Day</p>
          <p className="text-sm font-bold text-[#0A0A0A] leading-snug" style={{ fontFamily: 'Outfit, sans-serif' }}>"{quote.text}"</p>
          <p className="text-xs text-purple-600 font-semibold mt-1">— {quote.author}</p>
        </div>
      )}

      {/* Recent Posts */}
      {posts.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
              <Sparkles size={14} className="text-amber-500" />Talent Buzz
            </p>
            <Link to="/talent-buzz" className="text-xs text-blue-600 font-bold hover:underline" data-testid="buzz-widget-view-all">View All</Link>
          </div>
          <div className="space-y-3">
            {posts.slice(0, 3).map(post => {
              const typeConfig = POST_TYPE_CONFIG[post.post_type];
              const isSystem = post.author_id === 'system';
              return (
                <div key={post.id} className={`rounded-lg p-3 border ${typeConfig ? `${typeConfig.bg} border-[#0A0A0A]` : 'bg-slate-50 border-slate-100'}`} data-testid={`widget-post-${post.id}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    {isSystem ? (
                      <div className="w-6 h-6 rounded-md bg-[#0A0A0A] flex items-center justify-center text-[#FDE047] text-[9px] font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>HR</div>
                    ) : (
                      <div className="w-6 h-6 rounded-md bg-blue-100 border border-[#0A0A0A] flex items-center justify-center text-[9px] font-black">{post.author_name?.charAt(0)}</div>
                    )}
                    <span className="text-xs font-bold text-[#0A0A0A]">{isSystem ? 'HRassistance' : post.author_name}</span>
                    {typeConfig && <typeConfig.icon size={12} className={typeConfig.iconColor} />}
                    <span className="text-[10px] text-[#A3A3A3] ml-auto">{getTimeAgo(post.created_at)}</span>
                  </div>
                  <p className="text-xs text-[#404040] line-clamp-2">{post.content}</p>
                  {(post.reaction_count > 0 || (post.comment_count || 0) > 0) && (
                    <div className="flex items-center gap-3 mt-1.5 text-[10px] text-[#A3A3A3]">
                      {post.reaction_count > 0 && <span>{post.reaction_count} reaction{post.reaction_count !== 1 ? 's' : ''}</span>}
                      {(post.comment_count || 0) > 0 && <span>{post.comment_count} comment{post.comment_count !== 1 ? 's' : ''}</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
